import { formatShoppingDisplayAmount } from './shoppingDisplayText';

function normalizeShoppingToken(value = '') {
    return String(value || '')
        .trim()
        .toLocaleLowerCase('tr-TR')
        .replace(/ı/g, 'i')
        .replace(/ş/g, 's')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c');
}

const CANONICAL_UNITS = new Set(['adet', 'ad', 'tane', 'gr', 'g', 'kg', 'ml', 'lt', 'l']);
const MARKET_UNITS = new Set(['paket', 'kutu', 'koli', 'sise', 'şişe', 'file']);
const COUNT_PRODUCT_KEYWORDS = ['yumurta', 'kapsul', 'kapsül', 'bar', 'tablet'];
const VOLUME_PRODUCT_KEYWORDS = [
    'sut',
    'süt',
    'ayran',
    'su',
    'icecek',
    'içecek',
    'meyve suyu',
    'sirke',
    'sos',
    'krema',
    'yag',
    'yağ'
];

function isCanonicalUnit(unit = '') {
    return CANONICAL_UNITS.has(normalizeShoppingToken(unit));
}

function isMarketUnit(unit = '') {
    return MARKET_UNITS.has(normalizeShoppingToken(unit));
}

function resolveExpectedMeasurement({ name = '', unit = '' } = {}) {
    const normalizedName = normalizeShoppingToken(name);
    const normalizedUnit = normalizeShoppingToken(unit);

    if (COUNT_PRODUCT_KEYWORDS.some((keyword) => normalizedName.includes(normalizeShoppingToken(keyword)))) {
        return { measurementType: 'COUNT', expectedCanonicalUnit: 'adet' };
    }

    if (normalizedUnit === 'sise' || normalizedUnit === 'şişe') {
        return { measurementType: 'VOLUME', expectedCanonicalUnit: 'ml' };
    }

    if (VOLUME_PRODUCT_KEYWORDS.some((keyword) => normalizedName.includes(normalizeShoppingToken(keyword)))) {
        return { measurementType: 'VOLUME', expectedCanonicalUnit: 'ml' };
    }

    return { measurementType: 'WEIGHT', expectedCanonicalUnit: 'gr' };
}

function resolveDraftNormalization(item = {}) {
    const unit = String(item?.unit || 'adet').trim() || 'adet';
    const name = String(item?.name || item?.ingredientName || '').trim();

    if (!isMarketUnit(unit) || isCanonicalUnit(unit)) {
        return {
            needsClarification: false,
            measurementType: null,
            expectedCanonicalUnit: null,
            prompt: null
        };
    }

    const measurement = resolveExpectedMeasurement({ name, unit });
    return {
        needsClarification: true,
        ...measurement,
        prompt: `1 ${unit} kaç ${measurement.expectedCanonicalUnit}?`,
        helperText: `${name || 'Bu ürün'} için iç hesap ${measurement.expectedCanonicalUnit} bazında yapılacak.`
    };
}

function buildDraftNormalizationPayload(item = {}) {
    const normalization = resolveDraftNormalization(item);
    if (!normalization.needsClarification) return null;

    const marketAmount = Number(item?.amount || 0);
    const perUnitCanonicalAmount = Number(item?.perUnitCanonicalAmount || 0);
    if (!(marketAmount > 0) || !(perUnitCanonicalAmount > 0)) return null;

    const canonicalAmount = Number((marketAmount * perUnitCanonicalAmount).toFixed(2));
    const canonicalUnit = normalization.expectedCanonicalUnit;

    return {
        marketAmount,
        marketUnit: String(item?.unit || '').trim() || 'adet',
        measurementType: normalization.measurementType,
        canonicalAmount,
        canonicalUnit,
        conversionSource: 'USER_PROMPT',
        conversionConfidence: 'USER_CONFIRMED',
        resolutionStatus: 'USER_CONFIRMED',
        canonicalSourceSnapshot: {
            marketAmount,
            marketUnit: String(item?.unit || '').trim() || 'adet',
            userAnswer: perUnitCanonicalAmount,
            userAnswerUnit: canonicalUnit,
            resolvedCanonicalAmount: canonicalAmount,
            resolvedCanonicalUnit: canonicalUnit,
            measurementType: normalization.measurementType,
            source: 'USER_PROMPT'
        }
    };
}

function resolveDraftNormalizationPreview(item = {}) {
    const normalization = resolveDraftNormalization(item);
    if (!normalization.needsClarification) return null;

    const payload = buildDraftNormalizationPayload(item);
    if (!payload) {
        return {
            state: 'needs_input',
            text: normalization.prompt
        };
    }

    return {
        state: 'resolved',
        text: `≈ ${formatShoppingDisplayAmount(payload.canonicalAmount, payload.canonicalUnit)} olarak kullanılacak`
    };
}

export {
    isCanonicalUnit,
    isMarketUnit,
    resolveDraftNormalization,
    buildDraftNormalizationPayload,
    resolveDraftNormalizationPreview
};
